import { useMemo } from 'react';

import { useMessages } from '@zenbusiness/application-commons-intl';

import { RA_TASK_TYPES } from '#client/constants/enterpriseTasks';
import { useBusinessEntityInfo } from '#client/hooks/useBusinessEntityInfo';
import { useOrderTrackerStatus } from '#client/hooks/useOrderTrackerStatus';
import { formatEIN } from '#client/pages/BusinessProfile/helpers';

// formatEIN requires an fm (formatMessage) function for its null fallback;
// we handle the null case ourselves and pass a no-op for the formatting path.
const displayEIN = (ein, pendingLabel) => (ein ? formatEIN(ein, () => '') : pendingLabel);

function useEnterpriseProfileHeader({ enterprise } = {}) {
  const { loading: entityLoading, businessEntityInfo, hasError } = useBusinessEntityInfo();
  const { loading: orderLoading, orderTasksWithStatus } = useOrderTrackerStatus();

  const messages = useMessages({
    pendingValue: 'dashboard.enterpriseHome.profileHeader.pendingValue',
    registeredAgentLabel: 'dashboard.enterpriseHome.profileHeader.registeredAgentLabel',
    zenBusinessValue: 'dashboard.enterpriseHome.profileHeader.zenBusinessValue',
    einLabel: 'dashboard.enterpriseHome.profileHeader.einLabel',
    planLabel: 'dashboard.enterpriseHome.profileHeader.planLabel'
  });

  const statusItems = useMemo(() => {
    const taskMap = orderTasksWithStatus.reduce((acc, task) => {
      acc[task.type] = task;
      return acc;
    }, {});

    const raTask = RA_TASK_TYPES.map((type) => taskMap[type]).find(Boolean);
    const raActive = raTask?.status === 'active';

    return [
      {
        label: messages.registeredAgentLabel,
        value: raActive ? messages.zenBusinessValue : messages.pendingValue,
        isPending: !raActive
      },
      {
        label: messages.einLabel,
        value: displayEIN(businessEntityInfo?.ein, messages.pendingValue),
        copyable: true,
        isPending: !businessEntityInfo?.ein
      },
      {
        label: messages.planLabel,
        value: enterprise?.name,
        isEnterprisePlan: true
      }
    ];
  }, [businessEntityInfo, enterprise, messages, orderTasksWithStatus]);

  return {
    formationDate: businessEntityInfo?.formationDate,
    hasError,
    loading: entityLoading || orderLoading,
    name: businessEntityInfo?.name,
    nameInitials: businessEntityInfo?.nameInitials,
    statusItems
  };
}

export { useEnterpriseProfileHeader };
